import styled from 'styled-components';
import { Section } from '../../components/Section/Section';

/* =====================================================================
   Estilos do bloco Simulador de Financiamento.
   Portado de `simulador-financiamento/styles.css`. Cores via
   var(--token, fallback) — os tokens vêm da paleta ativa (className) que
   o Canva injeta. Breakpoints via classe (.lpfs-bp-tablet/.lpfs-bp-mobile)
   medida pelo próprio bloco (o preview mobile do builder não muda o viewport).
   ===================================================================== */
export const SimuladorSection = styled(Section)<Section>`
  width: 100%;

  /* tokens derivados (fallback = paleta verde institucional do protótipo) */
  --lpfs-primary: var(--general-primary, #12472d);
  --lpfs-bg: var(--general-background, #f5f5f2);
  --lpfs-border-gen: var(--general-border, #7f7f7f);
  --lpfs-text-title: var(--text-title, #12472d);
  --lpfs-text-body: var(--text-body, #186643);
  --lpfs-card-bg: var(--cards-background, #ffffff);
  --lpfs-card-title: var(--cards-title, #12472d);
  --lpfs-card-body: var(--cards-body, #186643);
  --lpfs-card-border: var(--cards-border, #7f7f7f);
  --lpfs-btn-bg: var(--buttons-background, #12472d);
  /* cor de frente sobre fundos na cor de destaque (accent/primário): usa a superfície clara do StyleGuide */
  --lpfs-on-accent: var(--cards-background, #ffffff);
  --lpfs-btn-border: var(--buttons-border, #12472d);

  --lpfs-accent: var(--general-primary, #12472d);
  --lpfs-surface: var(--cards-background, #ffffff);
  --lpfs-surface-hover: color-mix(in srgb, var(--general-primary, #12472d) 6%, var(--cards-background, #ffffff));
  --lpfs-success: #1e9e5a;
  --lpfs-error: #d6453d;
  /* CTA conectado ao StyleGuide (segue a paleta; override em Design > Cor dos botões) */
  --lpfs-cta-bg: var(--buttons-background, #cdd975);
  --lpfs-cta-text: var(--buttons-text, #12472d);
  /* texto secundário derivado do corpo (segue StyleGuide + override "Textos e descrições") */
  --lpfs-muted: color-mix(in srgb, var(--lpfs-text-body) 65%, transparent);
  --lpfs-hairline: color-mix(in srgb, var(--general-border, #7f7f7f) 45%, transparent);
  /* fundo dos campos (filled) conectado à superfície do StyleGuide */
  --lpfs-input-bg: color-mix(in srgb, var(--lpfs-text-body) 4%, var(--cards-background, #ffffff));
  /* cards/containers seguem o raio de CARD do StyleGuide (Aparência > Card) */
  --lpfs-radius-card: var(--design-card-border-radius, 2px);
  /* inputs, seletores e chips seguem o raio de CARD (superfície), não o do BOTÃO —
     assim a Aparência dos Botões não arredonda o dropdown de seleção */
  --lpfs-radius-input: var(--design-card-border-radius, 6px);
  --lpfs-radius-pill: 999px;
  --lpfs-shadow-lg: 0 24px 60px color-mix(in srgb, var(--general-primary, #12472d) 14%, transparent);
  --lpfs-line: 1px solid var(--lpfs-hairline);
  --lpfs-font-head: var(--typography-heading-font-family, inherit);
  --lpfs-font-text: var(--typography-text-font-family, inherit);
  --lpfs-maxw: 80rem;
  --lpfs-pad-x: 2rem;

  font-family: var(--lpfs-font-text);
  color: var(--lpfs-text-body);
  /* o fundo fica no MESMO elemento que recebe o padding (Section) */
  background-color: var(--general-background, #f5f5f2);
  -webkit-font-smoothing: antialiased;

  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }
  img {
    max-width: 100%;
    display: block;
  }

  .lpfs-fullbleed {
    width: 100%;
    padding: 0 var(--lpfs-pad-x);
    overflow-x: clip;
  }
  .lpfs-shell {
    max-width: var(--lpfs-maxw);
    margin: 0 auto;
  }
  .lpfs-container {
    padding: 48px;
    background: var(--cards-background, #ffffff);
    border-radius: 8px;
    border: var(--lpfs-line);
    box-shadow: 0 8px 40px color-mix(in srgb, var(--general-primary, #12472d) 7%, transparent);
  }

  /* Header — título/descrição seguem os tokens de tipografia do StyleGuide
     (mesmo padrão dos demais blocos). */
  .lpfs-header {
    margin-bottom: 28px;
  }
  .lpfs-title {
    font-family: var(--lpfs-font-head);
    font-weight: var(--typography-heading-font-weight, 600);
    font-size: var(--typography-heading-font-size-desktop, 40px);
    line-height: var(--typography-heading-line-height, 1.2);
    letter-spacing: var(--typography-heading-letter-spacing, 0);
    color: var(--lpfs-text-title);
    margin: 0;
  }
  .lpfs-block-desc {
    font-size: var(--typography-text-font-size-desktop, 16px);
    line-height: var(--typography-text-line-height, 1.5);
    color: var(--lpfs-muted);
    margin: 8px 0 0;
  }
  .lpfs-block-desc p {
    margin: 0 0 6px;
  }

  /* Stepper */
  .lpfs-stepper {
    display: flex;
    align-items: flex-start;
    gap: 0;
    margin-bottom: 44px;
    background: var(--cards-background, #ffffff);
    padding: 14px 0;
    border-bottom: var(--lpfs-line);
  }
  .lpfs-step {
    display: flex;
    align-items: center;
    flex: 1 1 0;
    min-width: 0;
  }
  .lpfs-step:last-child {
    flex: 0 0 auto;
  }
  .lpfs-step-btn {
    display: flex;
    align-items: center;
    gap: 10px;
    background: none;
    border: none;
    padding: 4px 0;
    font-family: var(--lpfs-font-text);
    cursor: default;
    color: var(--lpfs-muted);
    flex: 0 0 auto;
  }
  .lpfs-step.is-done .lpfs-step-btn {
    cursor: pointer;
  }
  .lpfs-step-dot {
    width: 34px;
    height: 34px;
    border-radius: var(--lpfs-radius-pill);
    display: grid;
    place-items: center;
    font-weight: 600;
    font-size: 14px;
    border: 2px solid var(--lpfs-hairline);
    background: var(--lpfs-card-bg);
    color: var(--lpfs-muted);
    flex: 0 0 auto;
    transition: background 0.25s ease, border-color 0.25s ease, color 0.25s ease;
  }
  .lpfs-step.is-active .lpfs-step-dot {
    background: var(--lpfs-accent);
    border-color: var(--lpfs-accent);
    color: var(--lpfs-on-accent);
  }
  .lpfs-step.is-done .lpfs-step-dot {
    background: color-mix(in srgb, var(--lpfs-accent) 14%, transparent);
    border-color: var(--lpfs-accent);
    color: var(--lpfs-accent);
  }
  .lpfs-step-label {
    font-size: 13px;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .lpfs-step.is-active .lpfs-step-label {
    font-weight: 600;
    color: var(--lpfs-text-title);
  }
  .lpfs-step.is-done .lpfs-step-label {
    color: var(--lpfs-text-body);
  }
  .lpfs-step-line {
    flex: 1 1 auto;
    height: 2px;
    margin: 0 14px;
    background: var(--lpfs-hairline);
    min-width: 20px;
  }
  .lpfs-step.is-done .lpfs-step-line {
    background: var(--lpfs-accent);
  }
  .lpfs-step.is-skipped .lpfs-step-dot {
    border-style: dashed;
    color: var(--lpfs-muted);
    background: var(--lpfs-surface);
  }
  .lpfs-step.is-skipped .lpfs-step-label {
    color: var(--lpfs-muted);
  }
  .lpfs-step-opt {
    display: block;
    font-size: 10px;
    font-weight: 400;
    line-height: 1;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--lpfs-muted);
    margin-top: 2px;
  }

  /* Stepper mobile */
  .lpfs-stepper-mobile {
    display: none;
  }
  .lpfs-sm-label {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    font-size: 13px;
    font-weight: 600;
    color: var(--lpfs-text-title);
    margin-bottom: 8px;
  }
  .lpfs-sm-step {
    color: var(--lpfs-muted);
    font-weight: 600;
  }
  .lpfs-progress {
    height: 4px;
    border-radius: var(--lpfs-radius-pill);
    background: var(--lpfs-hairline);
    overflow: hidden;
  }
  .lpfs-progress > span {
    display: block;
    height: 100%;
    background: var(--lpfs-accent);
    border-radius: inherit;
    transition: width 0.35s ease;
  }

  /* Grid principal */
  .lpfs-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 420px;
    gap: 4rem;
    align-items: start;
  }
  .lpfs-content {
    min-width: 0;
  }
  .lpfs-step-head {
    margin-bottom: 36px;
  }
  .lpfs-step-title {
    font-family: var(--lpfs-font-head);
    font-weight: 600;
    font-size: 26px;
    line-height: 1.15;
    color: var(--lpfs-text-title);
    margin: 0 0 8px;
  }
  .lpfs-step-sub {
    font-size: 15px;
    line-height: 1.5;
    color: var(--lpfs-muted);
    margin: 0;
  }
  .lpfs-fieldset {
    margin-bottom: 32px;
  }
  .lpfs-fieldset:last-of-type {
    margin-bottom: 0;
  }
  .lpfs-section-label {
    font-weight: 600;
    font-size: 13px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--lpfs-text-body);
    margin: 0 0 16px;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .lpfs-anim-in {
    animation: lpfs-fade-up 0.25s ease both;
  }
  @keyframes lpfs-fade-up {
    from {
      opacity: 0;
      transform: translateY(12px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  /* Painel do veículo */
  .lpfs-vehicle-panel {
    position: sticky;
    top: 24px;
    background: var(--lpfs-surface);
    border-radius: var(--lpfs-radius-card);
    padding: 8px 4px;
  }
  .lpfs-vp-img {
    aspect-ratio: 16 / 11;
    display: grid;
    place-items: center;
    overflow: hidden;
    margin-bottom: 20px;
  }
  .lpfs-vp-img img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
  .lpfs-vp-empty {
    color: var(--lpfs-muted);
    font-size: 14px;
    padding: 24px;
    text-align: center;
  }
  .lpfs-vp-name {
    font-family: var(--lpfs-font-head);
    font-weight: 600;
    font-size: 34px;
    line-height: 1;
    letter-spacing: -0.01em;
    color: var(--lpfs-card-title);
    margin: 0 0 6px;
  }
  .lpfs-vp-version {
    font-size: 14px;
    color: var(--lpfs-card-body);
    margin: 0 0 20px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-weight: 600;
  }
  .lpfs-vp-price-label {
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-weight: 600;
    color: var(--lpfs-muted);
    margin: 0;
  }
  .lpfs-vp-price {
    font-family: var(--lpfs-font-head);
    font-weight: 600;
    font-size: 30px;
    color: var(--lpfs-card-title);
    margin: 2px 0 0;
  }
  .lpfs-vp-divider {
    height: 1px;
    background: var(--lpfs-hairline);
    margin: 22px 0;
  }
  .lpfs-vp-rows {
    display: grid;
    gap: 11px;
  }
  .lpfs-vp-row {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    font-size: 14px;
    color: var(--lpfs-card-body);
  }
  .lpfs-vp-row strong {
    font-weight: 600;
    color: var(--lpfs-card-title);
  }
  .lpfs-vp-unit {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 22px;
    padding: 12px 14px;
    border: 1px solid var(--lpfs-hairline);
    background: var(--lpfs-surface-hover);
  }
  .lpfs-vp-unit-pin {
    flex: 0 0 auto;
    display: grid;
    place-items: center;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    background: var(--lpfs-accent);
    /* ícone sempre contrastando com o fundo do pino (não usar buttons-text, que pode ser igual ao accent) */
    color: var(--lpfs-surface);
  }
  .lpfs-vp-unit-info {
    display: flex;
    flex-direction: column;
    min-width: 0;
    gap: 1px;
  }
  .lpfs-vp-unit-label {
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-weight: 600;
    color: var(--lpfs-muted);
  }
  .lpfs-vp-unit-name {
    font-size: 14px;
    font-weight: 600;
    color: var(--lpfs-text-title);
  }
  .lpfs-vp-unit-row {
    font-size: 12px;
    line-height: 1.45;
    color: var(--lpfs-muted);
  }
  .lpfs-vp-unit-map {
    font-size: 12px;
    font-weight: 600;
    color: var(--lpfs-accent);
    text-decoration: underline;
    text-underline-offset: 3px;
    margin-top: 2px;
    transition: opacity 0.2s ease;
  }
  .lpfs-vp-unit-map:hover {
    opacity: 0.7;
  }
  /* No modo 360 o stage tem aspect próprio + dica abaixo — sem crop do box 16/11. */
  .lpfs-vp-img.is-360 {
    display: block;
    aspect-ratio: auto;
    overflow: visible;
  }

  /* Barra do veículo no mobile (sticky na parte inferior da área do bloco) */
  .lpfs-mobile-vehicle {
    display: none;
  }
  .lpfs-mv-bar {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    text-align: left;
    background: var(--lpfs-surface);
    border: var(--lpfs-line);
    border-radius: var(--lpfs-radius-card);
    padding: 12px;
    font-family: inherit;
  }
  .lpfs-mv-thumb {
    width: 64px;
    height: 44px;
    flex: 0 0 auto;
    border-radius: 8px;
    overflow: hidden;
    background: color-mix(in srgb, var(--lpfs-text-body) 5%, var(--lpfs-surface));
  }
  .lpfs-mv-thumb img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
  .lpfs-mv-info {
    flex: 1 1 auto;
    min-width: 0;
  }
  .lpfs-mv-name {
    font-weight: 600;
    font-size: 15px;
    color: var(--lpfs-card-title);
  }
  .lpfs-mv-price {
    font-weight: 600;
    font-size: 16px;
    color: var(--lpfs-card-title);
  }
  .lpfs-mv-more {
    flex: 0 0 auto;
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    color: var(--lpfs-accent);
    background: transparent;
    border: none;
    padding: 0;
    margin: 0;
    cursor: pointer;
    text-decoration: underline;
    text-underline-offset: 4px;
    transition: opacity 0.2s ease;
  }
  .lpfs-mv-more:hover {
    opacity: 0.7;
  }
  /* Detalhes abrem ABAIXO da barra, no mesmo box flutuante (a barra é sticky
     bottom — o box cresce para cima e os detalhes ficam sob a barra). */
  .lpfs-mv-detail {
    background: var(--lpfs-surface);
    border: var(--lpfs-line);
    border-top: none;
    margin-top: -1px;
    padding: 12px 12px 16px;
    border-radius: 0 0 var(--lpfs-radius-card) var(--lpfs-radius-card);
  }
  .lpfs-mobile-vehicle.is-open .lpfs-mv-bar {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }

  /* Inputs */
  .lpfs-field {
    margin-bottom: 22px;
  }
  .lpfs-label {
    display: block;
    font-weight: 600;
    font-size: 13px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--lpfs-text-body);
    margin-bottom: 8px;
  }
  .lpfs-label-hint {
    text-transform: none;
    letter-spacing: 0;
    font-weight: 400;
    color: var(--lpfs-muted);
    font-size: 11px;
    margin-left: 6px;
  }
  .lpfs-input {
    width: 100%;
    height: 52px;
    padding: 0 14px;
    font-family: inherit;
    font-size: 16px;
    font-weight: 500;
    color: var(--lpfs-text-title);
    background: var(--lpfs-input-bg);
    border: 1.5px solid var(--lpfs-border-gen);
    border-radius: var(--lpfs-radius-input);
    transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
    appearance: none;
  }
  .lpfs-input::placeholder {
    color: var(--lpfs-muted);
    opacity: 1;
  }
  .lpfs-input:focus {
    outline: none;
    border-color: var(--lpfs-primary);
    background: var(--lpfs-surface);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--lpfs-primary) 14%, transparent);
  }
  .lpfs-field.is-valid .lpfs-input {
    border-color: var(--lpfs-success);
  }
  .lpfs-field.is-error .lpfs-input {
    border-color: var(--lpfs-error);
  }
  .lpfs-error-msg {
    display: none;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: var(--lpfs-error);
    margin-top: 6px;
  }
  .lpfs-field.is-error .lpfs-error-msg {
    display: flex;
  }
  .lpfs-form-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px 40px;
  }
  .lpfs-form-grid .lpfs-field {
    margin-bottom: 18px;
  }
  .lpfs-col-2 {
    grid-column: span 2;
  }

  /* Select estilizado (RichSelect) — modelo/versão/unidade/seminovo */
  .lpfs-rs {
    position: relative;
  }
  .lpfs-rs-trigger {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    min-height: 52px;
    padding: 8px 14px;
    font-family: inherit;
    font-size: 16px;
    font-weight: 500;
    text-align: left;
    color: var(--lpfs-text-title);
    background: var(--lpfs-input-bg);
    border: 1.5px solid var(--lpfs-border-gen);
    border-radius: var(--lpfs-radius-input);
    cursor: pointer;
    transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
  }
  .lpfs-rs-trigger:focus-visible {
    outline: none;
    border-color: var(--lpfs-primary);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--lpfs-primary) 14%, transparent);
  }
  .lpfs-rs.is-open .lpfs-rs-trigger {
    border-color: var(--lpfs-primary);
    background: var(--lpfs-surface);
  }
  .lpfs-rs-trigger:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
  .lpfs-rs-current {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1 1 auto;
    min-width: 0;
  }
  .lpfs-rs-placeholder {
    flex: 1 1 auto;
    color: var(--lpfs-muted);
  }
  .lpfs-rs-caret {
    flex: 0 0 auto;
    color: var(--lpfs-muted);
    transition: transform 0.2s ease;
    display: grid;
    place-items: center;
  }
  .lpfs-rs.is-open .lpfs-rs-caret {
    transform: rotate(180deg);
  }
  .lpfs-rs-panel {
    position: absolute;
    z-index: 30;
    top: calc(100% + 6px);
    left: 0;
    right: 0;
    max-height: 320px;
    overflow-y: auto;
    margin: 0;
    padding: 6px;
    list-style: none;
    background: var(--lpfs-surface);
    border: 1.5px solid var(--lpfs-border-gen);
    border-radius: var(--lpfs-radius-input);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12);
  }
  .lpfs-rs-option {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 10px 10px;
    font-family: inherit;
    font-size: 15px;
    font-weight: 500;
    text-align: left;
    color: var(--lpfs-text-title);
    background: transparent;
    border: none;
    border-radius: calc(var(--lpfs-radius-input) - 2px);
    cursor: pointer;
    transition: background 0.15s ease;
  }
  .lpfs-rs-option:hover {
    background: var(--lpfs-surface-hover);
  }
  .lpfs-rs-option.is-selected {
    background: color-mix(in srgb, var(--lpfs-accent) 10%, transparent);
  }
  .lpfs-rs-media {
    flex: 0 0 auto;
    width: 72px;
    height: 48px;
    border-radius: 8px;
    overflow: hidden;
    display: grid;
    place-items: center;
    background: color-mix(in srgb, var(--lpfs-text-body) 5%, var(--lpfs-surface));
  }
  .lpfs-rs-media img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
  .lpfs-rs-media.is-logo {
    width: 40px;
    height: 40px;
    border-radius: 50%;
  }
  .lpfs-rs-monogram {
    flex: 0 0 auto;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    font-weight: 600;
    font-size: 16px;
    color: var(--lpfs-accent);
    background: color-mix(in srgb, var(--lpfs-accent) 12%, transparent);
  }
  .lpfs-rs-texts {
    display: flex;
    flex-direction: column;
    gap: 1px;
    min-width: 0;
  }
  .lpfs-rs-label {
    font-weight: 600;
    color: var(--lpfs-text-title);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .lpfs-rs-desc {
    font-size: 12px;
    color: var(--lpfs-muted);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .lpfs-field.is-error .lpfs-rs-trigger {
    border-color: var(--lpfs-error);
  }

  /* Viewer 360 (painel do veículo) */
  .lpfs-spin360 {
    display: flex;
    flex-direction: column;
    gap: 6px;
    width: 100%;
  }
  .lpfs-spin360-stage {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    aspect-ratio: 16 / 10;
    overflow: hidden;
    outline: none;
    user-select: none;
    touch-action: none;
    cursor: grab;
  }
  .lpfs-spin360-stage.is-dragging {
    cursor: grabbing;
  }
  .lpfs-spin360-stage img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    pointer-events: none;
    transition: opacity 0.15s ease;
  }
  .lpfs-spin360-stage img.is-hidden {
    opacity: 0;
  }
  .lpfs-spin360-skeleton {
    position: absolute;
    inset: 0;
    background: color-mix(in srgb, var(--lpfs-text-body) 6%, transparent);
    animation: lpfs-pulse 1.4s ease-in-out infinite;
  }
  @keyframes lpfs-pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
  .lpfs-spin360-hint {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    font-size: 11px;
    color: var(--lpfs-muted);
  }

  /* Chips */
  .lpfs-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }
  .lpfs-chip {
    font-family: inherit;
    font-size: 14px;
    font-weight: 600;
    padding: 11px 22px;
    border: 1.5px solid var(--lpfs-border-gen);
    border-radius: var(--lpfs-radius-input);
    background: var(--lpfs-surface);
    color: var(--lpfs-text-body);
    cursor: pointer;
    transition: all 0.18s ease;
  }
  .lpfs-chip:hover:not(:disabled):not(.is-active) {
    background: var(--lpfs-surface-hover);
  }
  .lpfs-chip:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    border-style: dashed;
    color: var(--lpfs-muted);
  }
  .lpfs-chip.is-active {
    background: var(--lpfs-accent);
    color: var(--lpfs-on-accent);
    border-color: var(--lpfs-accent);
  }

  /* Checkboxes / radios — padrão dos formulários do sistema:
     input nativo + accent-color (mesma abordagem do DynamicForm publicado). */
  .lpfs-checkgroup,
  .lpfs-radiogroup-inline {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 22px;
  }
  .lpfs-native-option {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 500;
    color: var(--lpfs-text-body);
    cursor: pointer;
  }
  .lpfs-native-check,
  .lpfs-native-radio {
    width: 18px;
    height: 18px;
    margin: 0;
    flex: 0 0 auto;
    cursor: pointer;
    accent-color: var(--lpfs-accent);
  }
  .lpfs-field.is-error .lpfs-native-check {
    outline: 1.5px solid var(--lpfs-error);
    outline-offset: 1px;
  }

  /* Checkbox LGPD */
  .lpfs-check {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    font-size: 14px;
    color: var(--lpfs-text-body);
    cursor: pointer;
    line-height: 1.4;
  }
  .lpfs-check .lpfs-native-check {
    margin-top: 2px;
  }
  .lpfs-check a {
    color: var(--lpfs-accent);
    text-decoration: underline;
  }
  .lpfs-lgpd-text {
    margin-top: 14px;
    padding-left: 34px;
    font-size: 12px;
    line-height: 1.55;
    color: var(--lpfs-muted);
  }
  .lpfs-lgpd-text p {
    margin: 0 0 10px;
  }
  .lpfs-lgpd-text p:last-child {
    margin: 0;
  }
  .lpfs-lgpd-text a {
    color: var(--lpfs-accent);
    text-decoration: underline;
  }

  /* Slider */
  .lpfs-slider-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 10px;
    flex-wrap: wrap;
  }
  .lpfs-money-input {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 52px;
    padding: 0 14px;
    background: var(--lpfs-input-bg);
    border: 1.5px solid var(--lpfs-border-gen);
    border-radius: var(--lpfs-radius-input);
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
  }
  .lpfs-money-input:focus-within {
    border-color: var(--lpfs-primary);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--lpfs-primary) 14%, transparent);
  }
  .lpfs-money-input span {
    color: var(--lpfs-muted);
    font-weight: 600;
  }
  .lpfs-money-input input {
    border: none;
    background: none;
    outline: none;
    font-family: var(--lpfs-font-head);
    font-weight: 600;
    font-size: 20px;
    color: var(--lpfs-text-title);
    width: 130px;
  }
  .lpfs-slider-wrap {
    position: relative;
    padding: 14px 0 6px;
  }
  .lpfs-range {
    -webkit-appearance: none;
    appearance: none;
    width: 100%;
    height: 6px;
    border-radius: var(--lpfs-radius-pill);
    outline: none;
    margin: 0;
    background: linear-gradient(
      to right,
      var(--lpfs-accent) 0% var(--lpfs-fill, 0%),
      var(--lpfs-hairline) var(--lpfs-fill, 0%) 100%
    );
  }
  .lpfs-range::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: var(--lpfs-accent);
    border: 4px solid var(--lpfs-surface);
    cursor: pointer;
  }
  .lpfs-range::-moz-range-thumb {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: var(--lpfs-accent);
    border: 4px solid var(--lpfs-surface);
    cursor: pointer;
  }
  .lpfs-slider-scale {
    display: flex;
    justify-content: space-between;
    font-size: 12px;
    color: var(--lpfs-muted);
    margin-top: 6px;
  }

  /* Display "a financiar" */
  .lpfs-display {
    border-top: 1px solid var(--lpfs-hairline);
    padding: 24px 0 4px;
    margin-top: 12px;
  }
  .lpfs-display-entry {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    padding-bottom: 16px;
    margin-bottom: 18px;
    border-bottom: 1px solid var(--lpfs-hairline);
    font-size: 14px;
  }
  .lpfs-display-entry > span {
    color: var(--lpfs-text-body);
    font-weight: 600;
  }
  .lpfs-display-entry strong {
    font-family: var(--lpfs-font-head);
    font-weight: 600;
    font-size: 18px;
    color: var(--lpfs-text-title);
  }
  .lpfs-display-label {
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-weight: 600;
    color: var(--lpfs-muted);
    margin: 0 0 10px;
  }
  .lpfs-display-value {
    font-family: var(--lpfs-font-head);
    font-weight: 600;
    font-size: 44px;
    line-height: 1;
    letter-spacing: -0.015em;
    color: var(--lpfs-text-title);
    margin: 0;
  }

  /* Card FIPE */
  .lpfs-fipe-card {
    background: color-mix(in srgb, var(--lpfs-success) 8%, var(--lpfs-surface));
    border: 1.5px solid color-mix(in srgb, var(--lpfs-success) 30%, transparent);
    border-radius: var(--lpfs-radius-card);
    padding: 18px 20px;
    margin: 4px 0 18px;
  }
  .lpfs-fipe-label {
    font-size: 13px;
    color: var(--lpfs-text-body);
    margin: 0;
  }
  .lpfs-fipe-value {
    font-family: var(--lpfs-font-head);
    font-weight: 600;
    font-size: 26px;
    color: var(--lpfs-text-title);
    margin: 4px 0 6px;
  }
  .lpfs-fipe-note {
    font-size: 12px;
    color: var(--lpfs-muted);
    margin: 0;
  }

  /* Resultado */
  .lpfs-trust {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 600;
    color: var(--lpfs-success);
    background: color-mix(in srgb, var(--lpfs-success) 10%, transparent);
    padding: 8px 14px;
    border-radius: var(--lpfs-radius-pill);
    margin-bottom: 18px;
  }
  .lpfs-result {
    border: 1px solid var(--lpfs-hairline);
    border-radius: var(--lpfs-radius-card);
    overflow: hidden;
    margin-bottom: 18px;
  }
  .lpfs-result-hero {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 26px 24px;
    background: color-mix(in srgb, var(--lpfs-accent) 6%, var(--lpfs-surface));
    border-bottom: 1px solid var(--lpfs-hairline);
  }
  .lpfs-result-label {
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-weight: 600;
    color: var(--lpfs-muted);
  }
  .lpfs-result-installment {
    font-family: var(--lpfs-font-head);
    font-weight: 600;
    font-size: 36px;
    line-height: 1;
    color: var(--lpfs-text-title);
  }
  .lpfs-result-installment sup {
    font-size: 0.4em;
    vertical-align: super;
  }
  .lpfs-result-grid {
    margin: 0;
    padding: 20px 24px;
    display: grid;
    gap: 12px;
  }
  .lpfs-dl-row {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    font-size: 14px;
    color: var(--lpfs-text-body);
  }
  .lpfs-dl-row strong {
    color: var(--lpfs-text-title);
  }

  /* Resumo */
  .lpfs-expand-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    font-weight: 600;
    color: var(--lpfs-accent);
    background: none;
    border: none;
    cursor: pointer;
    font-family: inherit;
    padding: 6px 0;
  }
  .lpfs-expand-btn svg {
    transition: transform 0.2s ease;
  }
  .lpfs-expand-btn.is-open svg {
    transform: rotate(180deg);
  }
  .lpfs-summary-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    padding-top: 8px;
  }
  .lpfs-summary-card {
    background: var(--lpfs-surface);
    border-radius: var(--lpfs-radius-card);
    border: var(--lpfs-line);
    padding: 24px;
  }
  .lpfs-sc-title {
    font-weight: 600;
    font-size: 13px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--lpfs-muted);
    margin: 0 0 12px;
  }
  .lpfs-sc-rows {
    display: grid;
    gap: 8px;
  }
  .lpfs-sc-row {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    font-size: 14px;
    color: var(--lpfs-card-body);
  }
  .lpfs-sc-row strong {
    color: var(--lpfs-card-title);
    text-align: right;
  }
  .lpfs-legal {
    font-size: 12px;
    line-height: 1.5;
    color: var(--lpfs-muted);
    background: color-mix(in srgb, var(--lpfs-text-body) 4%, transparent);
    padding: 14px 16px;
    max-height: 120px;
    overflow-y: auto;
    margin: 18px 0;
  }
  .lpfs-legal p {
    margin: 0 0 8px;
  }
  .lpfs-legal p:last-child {
    margin-bottom: 0;
  }
  .lpfs-legal a {
    color: var(--lpfs-accent);
  }

  /* Sucesso */
  .lpfs-success {
    display: flex;
    gap: 14px;
    align-items: flex-start;
    padding: 18px 20px;
    margin: 8px 0 4px;
    border: 1px solid color-mix(in srgb, var(--lpfs-success) 40%, transparent);
    background: color-mix(in srgb, var(--lpfs-success) 8%, var(--lpfs-surface));
    border-radius: var(--lpfs-radius-card);
  }
  .lpfs-success-icon {
    flex: 0 0 auto;
    width: 42px;
    height: 42px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    background: color-mix(in srgb, var(--lpfs-success) 16%, transparent);
    color: var(--lpfs-success);
  }
  .lpfs-success-title {
    font-weight: 600;
    font-size: 16px;
    color: var(--lpfs-text-title);
    margin: 0 0 2px;
  }
  .lpfs-success-text {
    font-size: 14px;
    color: var(--lpfs-text-body);
    margin: 0;
  }

  /* Botões */
  .lpfs-actions {
    display: flex;
    gap: 16px;
    align-items: center;
    margin-top: 40px;
    padding-top: 28px;
    border-top: 1px solid var(--lpfs-hairline);
    flex-wrap: wrap;
  }
  .lpfs-spacer {
    flex: 1 1 auto;
  }
  .lpfs-btn {
    font-family: var(--lpfs-font-text);
    font-weight: 600;
    font-size: 14px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    padding: 0 28px;
    height: 54px;
    cursor: pointer;
    transition: all 0.2s ease;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    border: 2px solid transparent;
    /* raio do botão segue o StyleGuide (Aparência > Design) */
    border-radius: var(--design-button-border-radius, 0);
    text-decoration: none;
  }
  .lpfs-btn-primary {
    background: var(--lpfs-cta-bg);
    color: var(--lpfs-cta-text);
    border-color: var(--lpfs-cta-bg);
  }
  .lpfs-btn-primary:hover:not(:disabled) {
    filter: brightness(1.04);
  }
  .lpfs-btn-primary:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
  .lpfs-btn-ghost {
    background: transparent;
    color: var(--lpfs-text-body);
    border-color: var(--lpfs-hairline);
  }
  .lpfs-btn-ghost:hover {
    background: var(--lpfs-surface-hover);
  }

  /* ===================== breakpoints (viewport / @media) =====================
     O builder renderiza o mobile num iframe de 360px (viewport real), então
     @media funciona no preview e no publicado — sem depender de medir a largura
     do bloco por JS. Mesmos estilos/visual da referência, só o gatilho muda. */
  @media (max-width: 1023px) {
    .lpfs-container {
      padding: 36px;
    }
    .lpfs-grid {
      grid-template-columns: 1fr;
    }
    .lpfs-vehicle-panel {
      display: none;
    }
    /* Barra sticky no rodapé da área do bloco (referência Geely): acompanha o
       scroll enquanto o bloco está em vista; sticky é limitado ao container. */
    .lpfs-mobile-vehicle {
      display: block;
      position: sticky;
      bottom: 0;
      z-index: 5;
      margin-top: 24px;
    }
    .lpfs-mv-bar {
      box-shadow: 0 -8px 24px rgba(0, 0, 0, 0.08);
    }
  }
  @media (max-width: 767px) {
    --lpfs-pad-x: 1rem;
    /* Caixa branca dos steps abaixo do cabeçalho (o header fica fora dela),
       full-bleed: ocupa a tela toda, sem raio nem respiro lateral em volta. */
    .lpfs-container {
      margin-inline: calc(-1 * var(--lpfs-pad-x));
      padding: 20px 16px;
      background: var(--cards-background, #ffffff);
      border: var(--lpfs-line);
      border-left: none;
      border-right: none;
      border-radius: 0;
      box-shadow: none;
    }
    .lpfs-title {
      font-size: var(--typography-heading-font-size-mobile, 32px);
    }
    .lpfs-block-desc {
      font-size: var(--typography-text-font-size-mobile, 16px);
    }
    .lpfs-step-title {
      font-size: 22px;
    }
    .lpfs-display-value {
      font-size: 28px;
    }
    /* Mobile mantém o stepper de círculos numerados (padrão do deploy do
       design); compacta escondendo os rótulos pra caber na largura do device. */
    .lpfs-step-label {
      display: none;
    }
    /* Barra + flutuante "Ver mais" full-bleed: compensam o padding interno do
       card dos steps para ocupar a largura toda da tela. */
    .lpfs-mobile-vehicle {
      margin-inline: -16px;
      margin-bottom: -20px;
    }
    .lpfs-mv-bar,
    .lpfs-mv-detail {
      border-left: none;
      border-right: none;
      border-radius: 0;
    }
    /* Sem o valor repetido acima, o divider dos valores não abre o flutuante. */
    .lpfs-mv-detail .lpfs-vp-divider:first-child {
      display: none;
    }
    .lpfs-form-grid,
    .lpfs-summary-grid {
      grid-template-columns: 1fr;
    }
    .lpfs-col-2 {
      grid-column: span 1;
    }
    /* Parcela estimada e valores com tamanho adequado ao mobile */
    .lpfs-result-hero {
      padding: 20px 16px;
    }
    .lpfs-result-installment {
      font-size: 26px;
    }
    .lpfs-result-grid {
      padding: 16px;
    }
    .lpfs-dl-row,
    .lpfs-sc-row {
      font-size: 13px;
    }
    /* Voltar / Continuar lado a lado (3+ botões quebram linha) */
    .lpfs-actions {
      flex-direction: row;
      align-items: center;
      flex-wrap: wrap;
      gap: 8px;
    }
    .lpfs-spacer {
      display: none;
    }
    .lpfs-btn {
      flex: 1 1 calc(50% - 4px);
      width: auto;
      padding: 0 12px;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    *,
    *::before,
    *::after {
      animation-duration: 0.001ms !important;
      transition-duration: 0.001ms !important;
    }
  }
`;
